import React from 'react';
import { getLocalState, setLocalState } from '../../localState';
import GameLayout from './GameLayout';
import BigQuestion from './BigQuestion';
import BigBoard from '../../components/BigBoard/BigBoard';
import LocalFileStorage from '../../LocalFileStorage';

export type Team = 'A' | 'B';

type BaseQuestion = {
	text: string;
	dailyDouble?: true;
	answer?: string;
}
type TextQuestion = BaseQuestion;
type ImageQuestion = BaseQuestion & { image: IDBValidKey; };
type AudioQuestion = BaseQuestion & { audio: IDBValidKey; };
export type Question =
	TextQuestion
	| ImageQuestion
	| AudioQuestion;

export type Category = {
	name: string;
	author?: string;
	questions: Question[];
}

// categoryIndex, questionIndex
export type RoundState = boolean[][];

export type Round = {
	levels: number[];
	categories: Category[];
} | {
	finalJeopardy: true;
	category: string;
	question: Question;
}

type Props = {};
type State = {};

class Game extends React.Component<Props, State> {
	readonly db: LocalFileStorage;

	constructor(props: Props) {
		super(props);

		this.db = new LocalFileStorage();
	}

	storageUpdate = () => this.forceUpdate();

	componentDidMount() {
		this.db.init().then(() => this.forceUpdate());

		window.addEventListener('storage', this.storageUpdate);
	}

	componentWillUnmount() {
		window.removeEventListener('storage', this.storageUpdate);
	}

	selectQuestion = (categoryIndex: number, questionIndex: number) => {
		setLocalState('selectedQuestionIndex', [categoryIndex, questionIndex]);
		this.forceUpdate();
	};

	getSelectedQuestion = (): {
		selectedQuestion: Question | null,
		selectedQuestionLevel: number | null,
	} => {
		const {
			currentRoundIndex,
			selectedQuestionIndex,
			rounds,
		} = getLocalState();
		const currentRound = rounds[currentRoundIndex];
		if ('finalJeopardy' in currentRound || selectedQuestionIndex === null) {
			return {
				selectedQuestion: null,
				selectedQuestionLevel: null,
			};
		}
		const [categoryIndex, questionIndex] = selectedQuestionIndex;
		const question = currentRound.categories[categoryIndex].questions[questionIndex];
		return {
			selectedQuestion: question,
			selectedQuestionLevel: currentRound.levels[questionIndex],
		};
	};

	render() {
		const {
			currentRoundIndex,
			roundState,
			scores,
			dailyDoubleWager,
			finalJeopardyWagers,
			rounds,
		} = getLocalState();

		const currentRound = rounds[currentRoundIndex];

		const categoryCount = 'finalJeopardy' in currentRound
			? (rounds[currentRoundIndex - 1] as {
				levels: number[];
				categories: Category[];
			}).categories.length
			: currentRound.categories.length;

		const questionCount = 'finalJeopardy' in currentRound
			? (rounds[currentRoundIndex - 1] as {
				levels: number[];
				categories: Category[];
			}).categories[0]?.questions.length
			: currentRound.categories[0]?.questions.length;

		if ('finalJeopardy' in currentRound) {
			const question = currentRound.question;
			return (
				<GameLayout
					categoryCount={categoryCount}
					questionCount={questionCount}
					scores={scores}
					finalJeopardyCategory={finalJeopardyWagers === null ? currentRound.category : null}
				>
					<BigQuestion
						question={question}
						dailyDoubleWager={null}
						finalJeopardy={true}
						finalJeopardyWagers={finalJeopardyWagers}
						localFileStorage={this.db}
					/>
				</GameLayout>
			);
		}

		const { categories, levels } = currentRound;
		const { selectedQuestion } = this.getSelectedQuestion();

		return (
			<GameLayout
				categoryCount={categoryCount}
				questionCount={questionCount}
				scores={scores}
				finalJeopardyCategory={null}
			>
				{selectedQuestion ? (
					<BigQuestion
						question={selectedQuestion}
						dailyDoubleWager={dailyDoubleWager}
						finalJeopardy={false}
						finalJeopardyWagers={null}
						localFileStorage={this.db}
					/>
				) : (
					<BigBoard
						categories={categories}
						levels={levels}
						roundState={roundState}
						selectQuestion={this.selectQuestion}
					/>
				)}
			</GameLayout>
		);
	}
}

export default Game;
